import {
  Component,
  OnDestroy,
  OnInit,
  WritableSignal,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { getCurrentTimeBasedOnView } from 'src/app/crm/functions/controller-note-functions';
import { Entry } from 'src/app/model/controllerNotes/entry';
import { ControllerNotesBuilderService } from 'src/app/services/builder/controller-notes-builder.service';
import { emptyEntry } from 'src/app/utils/utils';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { NoteEditorComponent } from 'src/app/core/components/note-editor.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ProgressSpinner } from '@openng/optimus-ui/progressspinner';

@Component({
  selector: 'app-entry-form',
  templateUrl: './entry-form.component.html',
  styleUrls: ['./entry-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DatePicker,
    NoteEditorComponent,
    ButtonDirective,
    ProgressSpinner,
  ],
})
export class EntryFormComponent implements OnInit, OnDestroy {
  protected _fb = inject(FormBuilder);
  protected _controllerNoteBuilder = inject(ControllerNotesBuilderService);

  config = inject(DynamicDialogConfig);
  ref = inject(DynamicDialogRef);

  readonly entryToEdit: WritableSignal<Entry | null> = signal(null);
  readonly formType: WritableSignal<string | null> = signal(null);
  readonly minDateTime: WritableSignal<Date | null> = signal(new Date());
  readonly isAorView: WritableSignal<boolean> = signal(false);
  readonly aor: WritableSignal<string | null> = signal(null);
  readonly isLoading: WritableSignal<boolean> = signal(false);

  entryFormGroup: FormGroup;
  entryUpdated: boolean = false;

  constructor() {
    this.entryFormGroup = this._fb.group({
      entryTime: [
        getCurrentTimeBasedOnView(this.aor(), this.isAorView()),
        Validators.required,
      ],
      entryText: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.isLoading.set(true);
    this.entryToEdit.set(this.config?.data?.entry);
    this.minDateTime.set(this.config?.data?.minDateTime);
    this.formType.set(this.config?.data?.formType);
    this.isAorView.set(this.config?.data?.isAorView);
    this.aor.set(this.config?.data?.aor);

    this.updateFields();
  }

  ngOnDestroy(): void {
    if (this.ref) {
      this.ref.close();
    }
  }

  onSubmit() {
    if (!this.entryFormGroup.valid) {
      // failsafe to prevent inspect element from making the submit button enabled
      console.log('Form was somehow submitted without being valid!');
      return;
    }

    let eventObj = {
      ...this.entryFormGroup.value,
      entryFormType: this.formType(),
      version: this.entryToEdit()?.version,
      createdBy: this.entryToEdit()?.createdBy,
      id: this.entryToEdit()?.id,
    };

    this.ref.close(eventObj);
  }

  updateFields() {
    const entryToEdit = this.entryToEdit();
    if (entryToEdit !== emptyEntry) {
      // Patch in values to the form group
      this.entryFormGroup.patchValue({
        entryTime: entryToEdit?.time!,
        entryText: entryToEdit?.note,
      });
    } else {
      this.entryFormGroup.patchValue({
        entryTime: getCurrentTimeBasedOnView(this.aor(), this.isAorView()),
        entryText: '',
      });
    }

    this.isLoading.set(false);
  }

  validateForm() {
    this.entryFormGroup.updateValueAndValidity();
  }
}
